/**
 * OrgUserSearch — Autocomplete search for organization members
 *
 * Fetches org members via organizationService.getMembers and provides
 * a search-by-name/email dropdown. Shows name, department, role.
 */
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { getMembersLight } from '../../services/organizationService';
import type { OrgMember } from '../../services/organizationService';
import { useOrganizationId } from '../../contexts/WorkspaceContext';

interface OrgUserSearchProps {
  /** Currently selected user_id */
  value: string | null;
  /** Called when user selects a member */
  onSelect: (member: OrgMember | null) => void;
  /** Optional user IDs to exclude (e.g. already in project) */
  excludeIds?: string[];
  /** Placeholder text */
  placeholder?: string;
}

export const OrgUserSearch: React.FC<OrgUserSearchProps> = ({
  value,
  onSelect,
  excludeIds = [],
  placeholder = 'Buscar por nome ou e-mail...',
}) => {
  const orgId = useOrganizationId();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: members = [] } = useQuery({
    queryKey: ['org-members', orgId],
    queryFn: () => getMembersLight(orgId),
    enabled: !!orgId,
    staleTime: 120_000,
  });

  const excludeSet = useMemo(() => new Set(excludeIds), [excludeIds]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return members
      .filter(m => !excludeSet.has(m.user_id))
      .filter(m => {
        if (!q) return true;
        return (
          (m.user_name?.toLowerCase().includes(q)) ||
          (m.user_email?.toLowerCase().includes(q)) ||
          (m.department_name?.toLowerCase().includes(q))
        );
      })
      .slice(0, 20);
  }, [members, search, excludeSet]);

  const selected = useMemo(
    () => (value ? members.find(m => m.user_id === value) ?? null : null),
    [value, members],
  );

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = useCallback((m: OrgMember) => {
    onSelect(m);
    setSearch('');
    setOpen(false);
  }, [onSelect]);

  const handleClear = useCallback(() => {
    onSelect(null);
    setSearch('');
  }, [onSelect]);

  // If a user is selected, show their info
  if (selected) {
    return (
      <div className="flex items-center justify-between rounded border border-slate-200 bg-white px-2.5 py-2 dark:border-slate-700 dark:bg-slate-800">
        <div className="min-w-0">
          <p className="text-[12px] font-medium text-slate-800 dark:text-slate-200 truncate">
            {selected.user_name}
          </p>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
            {[selected.department_name, selected.role_name].filter(Boolean).join(' · ') || selected.user_email}
          </p>
        </div>
        <button
          onClick={handleClear}
          className="ml-2 shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700"
          title="Remover seleção"
        >
          <X size={12} />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={e => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full rounded border border-slate-200 bg-white px-2.5 py-2 pl-7 text-[12px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:placeholder:text-slate-500 dark:focus:border-blue-500"
          autoComplete="off"
        />
      </div>

      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
          {filtered.map(m => (
            <button
              key={m.user_id}
              onClick={() => handleSelect(m)}
              className="flex w-full items-center gap-2 px-2.5 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50"
            >
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-medium text-slate-800 dark:text-slate-200 truncate">
                  {m.user_name}
                </p>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                  {[m.department_name, m.role_name].filter(Boolean).join(' · ') || m.user_email}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {open && search && filtered.length === 0 && (
        <div className="absolute z-50 mt-1 w-full rounded border border-slate-200 bg-white px-3 py-3 text-center text-[11px] text-slate-400 shadow-lg dark:border-slate-700 dark:bg-slate-800">
          Nenhum membro encontrado
        </div>
      )}
    </div>
  );
};

export default OrgUserSearch;
